// CF-DRAGONTIGER-SEPARATE-APP-V1 — build-time app variant selector.
//
// The Chinese "金龙虎" (Dragon/Tiger) product is the SAME codebase as CricketFlip,
// selected at BUILD time via the Vite env var `VITE_APP_VARIANT=dragontiger`.
//
//   - `dragontiger` → force language to zh, apply the red/gold `[data-theme]`,
//                     show the 金龙虎 brand.
//   - `german`      → force language to de, apply the black/gold `[data-theme]`,
//                     show the „Kopf oder Zahl" brand (CF-GERMAN-VARIANT-V1).
//   - default       → byte-identical current behavior: gold/dark theme,
//                     Telegram-detected language, "CricketFlip" brand.
//
// IMPORTANT: the theme is VARIANT-gated, NOT language-gated. A zh user on the
// normal cricketflip app must NOT get the red theme — only the `dragontiger`
// build does. Read once at module load (same pattern as VITE_API_URL in App.tsx).

/** The exhaustive set of build variants. Anything else FAILS CLOSED to default. */
export type AppVariant = 'default' | 'dragontiger' | 'german' | 'brazil';

/**
 * STRICT, fail-closed variant selector. Maps a raw `VITE_APP_VARIANT` value to a
 * known {@link AppVariant}. Any unknown/missing/malformed value resolves to
 * 'default' — a typo can NEVER silently flip the app into a branded variant, and
 * the branded variants are reached ONLY by their exact lowercase token. Pure +
 * side-effect-free so the variant/guard matrix is unit-testable without Vite.
 */
export function selectVariant(raw: unknown): AppVariant {
  if (raw === 'dragontiger') return 'dragontiger';
  if (raw === 'german') return 'german';
  if (raw === 'brazil') return 'brazil';
  return 'default';
}

// Read defensively: under Vite, import.meta.env is injected at build time. Under a
// plain Node import (e.g. the framework-free variant-check.mts test, run via
// --experimental-strip-types), import.meta.env is undefined — the optional chain
// then yields undefined, which selectVariant() maps to 'default' (fail closed).
export const APP_VARIANT: AppVariant = selectVariant(import.meta.env?.VITE_APP_VARIANT);

/** True only for the build produced with VITE_APP_VARIANT=dragontiger. */
export const isDragonTiger = APP_VARIANT === 'dragontiger';

/** True only for the build produced with VITE_APP_VARIANT=german (Kopf oder Zahl). */
export const isGerman = APP_VARIANT === 'german';

/** True only for the build produced with VITE_APP_VARIANT=brazil (MoedaFlip · Cara ou Coroa). */
export const isBrazil = APP_VARIANT === 'brazil';

/**
 * Variant-aware product brand name. The branded builds must NEVER surface the
 * English "CricketFlip" name to their players (PL #8640 — a session-timeout dialog
 * leaked it). 金龙虎 for the dragontiger build, „Münzo" for the german build
 * (CF-GERMAN-MUENZO-REBRAND-V1 — PL-approved rename; „Kopf oder Zahl" is kept as
 * the german subtitle/tagline, not the brand name), CricketFlip for the default
 * build.
 */
export const BRAND_NAME = isDragonTiger
  ? '金龙虎'
  : isGerman
    ? 'Münzo'
    : isBrazil
      ? 'MoedaFlip'
      : 'CricketFlip';

/**
 * Variant-aware support address. The branded builds must NOT surface the
 * cricketflip.com inbox to their players (same leak class as BRAND_NAME — a
 * lowercase email is never folded by brandize()). Selected at build time like the
 * brand name. CF-GERMAN-MUENZO-REBRAND-V1: the german build uses the Münzo inbox.
 * The dragontiger + default builds keep their current cricketflip.com value
 * (unchanged rendered output).
 */
export const SUPPORT_EMAIL = isDragonTiger
  ? 'support@cricketflip.com'
  : isGerman
    ? 'support@muenzo.xyz' // CF-GERMAN: muenzo.xyz registered (Cloudflare 2026-06-21); set up email routing for this inbox
    : 'support@cricketflip.com';

/**
 * Variant-aware Telegram entry-CTA targets. The branded gate screens (beta + browser gate) must NOT
 * route their players to the CricketFlip channel/bot. CF-GERMAN-MUENZO-REBRAND-V1 (Scorer R2 1233):
 * the german (Münzo) build points its gate CTAs at the Münzo channel/bot — the bot URL is also the
 * german bot-auth target. The exact german handles are confirmed when PL creates the German bot at
 * activation (same open dependency as SUPPORT_EMAIL). default + dragontiger keep their current handles.
 */
export const TELEGRAM_CHANNEL_URL = isGerman
  ? 'https://t.me/muenzo_official' // CF-GERMAN: confirm PL's actual German channel at activation
  : 'https://t.me/cricketflip_official';
export const TELEGRAM_BOT_URL = isGerman
  ? 'https://t.me/muenzo_bot' // CF-GERMAN: confirm PL's actual German bot at activation (bot-auth target)
  : isBrazil
    ? 'https://t.me/moedaflip_bot' // CF-BRAZIL: confirm PL's actual MoedaFlip bot at activation (bot-auth target)
    : 'https://t.me/cricketflip_bot';
